import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { TextInput } from 'src/design/atoms/TextInput';
import { Textarea } from 'src/design/atoms/Textarea';
import { IconCalendarCheck, IconClock } from '@tabler/icons-react';
import React from 'react';
import { WbsAdjustmentDto } from 'src/api/FiretrolApiClient';
import { useTimesheetById } from 'src/api/queries/timesheets/useTimesheet';
import { useJobs } from 'src/hooks/useJobs';
import { getErrorMessage, formatDate, formatDateTime } from 'src/utils';
import { getCurrentWorkWeek } from 'src/utils/workWeek';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { LinkButton } from 'src/design/atoms/LinkButton';

interface SystemAdjustmentPanelProps {
  adjustment: WbsAdjustmentDto;
  onClose: () => void;
}

export const SystemAdjustmentPanel: React.FC<SystemAdjustmentPanelProps> = ({
  adjustment,
  onClose,
}) => {
  const { selectedJob } = useJobs();
  // Get first items from arrays (backend returns arrays now)
  const timesheetRow = adjustment.timesheetRows?.[0];
  const dailyProgressItem = adjustment.dailyProgressItems?.[0];

  const {
    data: timesheet,
    isLoading: timesheetLoading,
    error,
  } = useTimesheetById(timesheetRow?.timesheetId);
  const timesheetError = error
    ? getErrorMessage(error, 'Failed to load timesheet')
    : null;

  // Build timesheet link if timesheet data is available
  const timesheetLink = React.useMemo(() => {
    if (timesheet && selectedJob) {
      const year = timesheet.year ?? getCurrentWorkWeek().year;
      return `/jobs/${selectedJob.id}/timesheets/user/${timesheet.userId}/week/${year}/${timesheet.weekId}`;
    }
    return null;
  }, [timesheet, selectedJob]);

  // Build daily progress link if daily progress item has a dailyId
  const dailyProgressLink = React.useMemo(() => {
    if (dailyProgressItem?.dailyId && selectedJob) {
      return `/jobs/${selectedJob.id}/dailys/view/${dailyProgressItem.dailyId}`;
    }
    return null;
  }, [dailyProgressItem?.dailyId, selectedJob]);

  return (
    <Stack gap="md">
      <Title order={5} mb={0}>
        System Generated Adjustment Details
      </Title>

      {timesheetRow ? (
        <Alert icon={<IconClock />} color="blue">
          <Text>
            This adjustment was generated from timesheet entry data. The
            timesheet can be accessed through the Timesheets module.
          </Text>
          {timesheetLoading && <Text size="sm">Loading timesheet...</Text>}
          {timesheetError && (
            <Text size="sm" c="red">
              Error: {timesheetError}
            </Text>
          )}
          {timesheetLink && (
            <LinkButton to={timesheetLink}>View Timesheet</LinkButton>
          )}
        </Alert>
      ) : dailyProgressItem ? (
        <Alert icon={<IconCalendarCheck />} color="blue">
          <Text>
            This adjustment was generated from a daily progress entry. The
            progress entry can be accessed through the Daily Progress module.
          </Text>
          {dailyProgressLink ? (
            <LinkButton to={dailyProgressLink}>View Daily Progress</LinkButton>
          ) : (
            <Text>This daily progress entry is not tied to a daily.</Text>
          )}
        </Alert>
      ) : (
        <Alert color="blue" title="Information">
          This adjustment was automatically generated by the system and cannot
          be edited manually.
        </Alert>
      )}

      <SimpleGrid cols={3}>
        <TextInput
          label="Type"
          value={adjustment.adjustmentType?.name || 'Unknown Type'}
          disabled
        />
        <TextInput
          label="Amount"
          value={adjustment.amount.toString()}
          disabled
        />

        <TextInput
          label="Adjustment Date"
          value={formatDate(adjustment.adjustmentDate)}
          disabled
        />
      </SimpleGrid>

      <Textarea
        label="Description"
        value={adjustment.description || 'No description provided'}
        disabled
        autosize
        minRows={2}
      />

      <SimpleGrid cols={2}>
        <TextInput
          label="Created By"
          value={adjustment.createdBy?.fullName || 'System'}
          disabled
        />

        <TextInput
          label="Created On"
          value={formatDateTime(adjustment.createdAt, 'MM/DD/YYYY hh:mm A')}
          disabled
        />
      </SimpleGrid>

      <Group justify="flex-end" mt="md">
        <BasicButton color="black" variant="light" onClick={onClose}>
          Close
        </BasicButton>
      </Group>
    </Stack>
  );
};
